import { alert } from '@/services/alert';
import { createTimelineNote, updateTimelineNote, deleteTimelineNote } from '@/services/timeline-notes';
import { isProvider } from '@bariendo/lib/profile';
import { createReference } from '@medplum/core';
import { Communication, Encounter, Reference } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useState } from 'react';

export function useTimelineNote() {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isDeletingNote, setIsDeletingNote] = useState(false);

  const createNote = async (
    timelineItemReference: Reference<Communication | Encounter>,
    content: string,
    richTextContent?: object
  ) => {
    if (!isProvider(profile)) {
      alert.error("We couldn't find your clinician profile.");
      return false;
    }

    try {
      setIsSavingNote(true);

      const communication = await medplum.readReference(timelineItemReference);
      await createTimelineNote(medplum, {
        currentUser: profile,
        communication: createReference(communication),
        content,
        richTextContent,
      });

      return true;
    } catch {
      alert.error("Couldn't save note");
      return false;
    } finally {
      setIsSavingNote(false);
    }
  };

  const editNote = async (noteReference: Reference<Communication>, content: string, richTextContent?: object) => {
    if (!isProvider(profile)) {
      alert.error("We couldn't find your clinician profile.");
      return false;
    }

    try {
      setIsEditingNote(true);

      await updateTimelineNote(medplum, {
        noteRef: noteReference,
        content,
        richTextContent,
      });

      return true;
    } catch {
      alert.error("Couldn't update note");
      return false;
    } finally {
      setIsEditingNote(false);
    }
  };

  const deleteNote = async (noteReference: Reference<Communication>) => {
    if (!isProvider(profile)) {
      alert.error("We couldn't find your clinician profile.");
      return false;
    }

    try {
      setIsDeletingNote(true);

      await deleteTimelineNote(medplum, {
        noteRef: noteReference,
      });

      return true;
    } catch {
      alert.error("Couldn't delete note");
      return false;
    } finally {
      setIsDeletingNote(false);
    }
  };

  return {
    isSavingNote,
    isEditingNote,
    isDeletingNote,
    createNote,
    editNote,
    deleteNote,
  };
}
